import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { updateUiState } from 'redux/slices/uiSlice';
import { startTour } from '@/redux/slices/productTourSlice';

/**
 * The one-off, session-scoped notices the Dashboard layout is responsible for:
 * dismissing the post-login toast once the user navigates away from where it
 * was raised, opening the forced-turned-off email-sync dialog, and auto-
 * starting the getting-started product tour on a first login.
 *
 * Split out of `Dashboard.js` in Phase 6; called unconditionally, so
 * `loginToastShownAtPath`, `gettingStartedTourAutoStartedRef` and all three
 * effects keep the lifetime they had inline.
 *
 * The two login-toast effects must stay in this order: the first records the
 * path the toast was raised on, the second compares the current path against
 * that record. Swapping them would dismiss the toast on the render that
 * raised it.
 */
const useDashboardSessionNotices = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { showLoginToast, showOnboardingDialogV2 } = useSelector(
    (state) => state.uiStates,
  );
  const { completedTourIds, dismissedTourIds } = useSelector(
    (state) => state.productTour,
  );
  const userDetails = useSelector((state) => state.userDetails.userDetails);
  const loginToastShownAtPath = useRef(null);

  useEffect(() => {
    if (showLoginToast) {
      loginToastShownAtPath.current = location.pathname;
    }
  }, [showLoginToast]);

  useEffect(() => {
    if (
      showLoginToast &&
      loginToastShownAtPath.current &&
      location.pathname !== loginToastShownAtPath.current &&
      location.pathname !== '/action-plan'
    ) {
      dispatch(updateUiState({ key: 'showLoginToast', value: false }));
    }
  }, [location.pathname]);

  useEffect(() => {
    if (
      (userDetails?.forcedTurnedOffGmailAutoSync ||
        userDetails?.forcedTurnedOffGoogleGmailAutoSync ||
        userDetails?.forcedTurnedOffOutlookEmailAutoSync) &&
      !userDetails?.userSetting?.hideForcedTurnedOffEmailServiceAutoSyncDialog
    ) {
      dispatch(
        updateUiState({
          key: 'showForcedTurnedOffFeature',
          value: userDetails?.forcedTurnedOffGmailAutoSync
            ? 'gmail'
            : userDetails?.forcedTurnedOffOutlookEmailAutoSync
            ? 'outlook'
            : 'google',
        }),
      );
    }
  }, [window.location.href, userDetails]);

  // Auto-start the "getting-started" product tour on a user's first login,
  // deferring until the existing 7-step onboarding wizard (showOnboardingDialogV2)
  // is no longer open. Fires at most once per mount.
  //
  // The flag is tri-state: `null` until `/us/org/configs` resolves, then
  // `true`/`false`. Only an explicit `false` means "the wizard is not coming".
  // Testing truthiness would treat the pre-config window as a settled no, start
  // a tour into it, and then have the wizard open fullscreen on top of it.
  const gettingStartedTourAutoStartedRef = useRef(false);
  useEffect(() => {
    if (gettingStartedTourAutoStartedRef.current) return;
    if (showOnboardingDialogV2 !== false) return;
    const loginCount = localStorage.getItem('loginCount');
    if (loginCount !== '1') return;
    if (
      completedTourIds?.includes('getting-started') ||
      dismissedTourIds?.includes('getting-started')
    ) {
      return;
    }
    gettingStartedTourAutoStartedRef.current = true;
    dispatch(startTour('getting-started'));
  }, [showOnboardingDialogV2, completedTourIds, dismissedTourIds, dispatch]);
};

export default useDashboardSessionNotices;
